import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import {
  ArrowDown,
  ArrowUp,
  Globe,
  Monitor,
  MousePointer,
  Percent,
  Smartphone,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { publisherApi } from '../lib/api'

type TimeRange = '24h' | '7d' | '30d' | '90d'

interface StatsData {
  totalClicks: number
  uniqueClicks: number
  conversions: number
  conversionRate: number
  earnings: number
  averageEPC: number
  topCountries: Array<{ country: string; clicks: number; earnings: number }>
  topDevices: Array<{ device: string; percentage: number }>
  dailyStats: Array<{
    date: string
    clicks: number
    uniqueClicks: number
    conversions: number
    earnings: number
  }>
  hourlyStats: Array<{
    hour: string
    clicks: number
    conversions: number
  }>
  browserStats: Array<{
    browser: string
    clicks: number
    percentage: number
  }>
  osStats: Array<{
    os: string
    clicks: number
    percentage: number
  }>
}

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4']

export function Statistics() {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d')
  const [selectedMetric, setSelectedMetric] = useState<'clicks' | 'conversions' | 'earnings'>(
    'clicks',
  )

  const { data: stats, isLoading } = useQuery({
    queryKey: ['statistics', timeRange],
    queryFn: () => publisherApi.getStatistics(timeRange),
  })

  if (isLoading || !stats) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map(() => (
              <div
                key={Math.random().toString(36).substring(7)}
                className="h-32 bg-gray-200 rounded-xl"
              ></div>
            ))}
          </div>
          <div className="h-96 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    )
  }

  const changeFromPrevious = (current: number, previous: number) => {
    if (previous === 0) return { value: 100, trend: 'up' as const }
    const change = ((current - previous) / previous) * 100
    return {
      value: Math.abs(change),
      trend: change >= 0 ? ('up' as const) : ('down' as const),
    }
  }

  const metricsCards = [
    {
      title: 'Total Clicks',
      value: stats.totalClicks.toLocaleString(),
      change: changeFromPrevious(stats.totalClicks, stats.totalClicks * 0.85),
      icon: MousePointer,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Unique Visitors',
      value: stats.uniqueClicks.toLocaleString(),
      change: changeFromPrevious(stats.uniqueClicks, stats.uniqueClicks * 0.9),
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Conversions',
      value: stats.conversions.toLocaleString(),
      change: changeFromPrevious(stats.conversions, stats.conversions * 0.8),
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Conversion Rate',
      value: `${stats.conversionRate.toFixed(2)}%`,
      change: changeFromPrevious(stats.conversionRate, stats.conversionRate * 0.95),
      icon: Percent,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
  ]

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistics & Analytics</h1>
          <p className="text-gray-600 mt-1">Track your performance and optimize your campaigns</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setTimeRange('24h')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              timeRange === '24h'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            24H
          </button>
          <button
            type="button"
            onClick={() => setTimeRange('7d')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              timeRange === '7d'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            7D
          </button>
          <button
            type="button"
            onClick={() => setTimeRange('30d')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              timeRange === '30d'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            30D
          </button>
          <button
            type="button"
            onClick={() => setTimeRange('90d')}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              timeRange === '90d'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            90D
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricsCards.map((metric) => (
          <div
            key={metric.title}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={clsx('p-2 rounded-lg', metric.bgColor)}>
                <metric.icon className={clsx('w-5 h-5', metric.color)} />
              </div>
              <div className={clsx('flex items-center text-sm font-medium')}>
                {metric.change.trend === 'up' ? (
                  <>
                    <ArrowUp className="w-4 h-4 text-green-600 mr-1" />
                    <span className="text-green-600">+{metric.change.value.toFixed(1)}%</span>
                  </>
                ) : (
                  <>
                    <ArrowDown className="w-4 h-4 text-red-600 mr-1" />
                    <span className="text-red-600">-{metric.change.value.toFixed(1)}%</span>
                  </>
                )}
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
            <p className="text-sm text-gray-600 mt-1">{metric.title}</p>
          </div>
        ))}
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Performance Overview</h2>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setSelectedMetric('clicks')}
              className={clsx(
                'px-3 py-1 rounded text-sm font-medium transition-colors',
                selectedMetric === 'clicks'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              Clicks
            </button>
            <button
              type="button"
              onClick={() => setSelectedMetric('conversions')}
              className={clsx(
                'px-3 py-1 rounded text-sm font-medium transition-colors',
                selectedMetric === 'conversions'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              Conversions
            </button>
            <button
              type="button"
              onClick={() => setSelectedMetric('earnings')}
              className={clsx(
                'px-3 py-1 rounded text-sm font-medium transition-colors',
                selectedMetric === 'earnings'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-100',
              )}
            >
              Earnings
            </button>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={stats.dailyStats}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
            <YAxis stroke="#9ca3af" fontSize={12} />
            <Tooltip />
            {selectedMetric === 'clicks' && (
              <>
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="#6366f1"
                  fill="#6366f1"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="uniqueClicks"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.1}
                  strokeWidth={2}
                />
              </>
            )}
            {selectedMetric === 'conversions' && (
              <Area
                type="monotone"
                dataKey="conversions"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            )}
            {selectedMetric === 'earnings' && (
              <Area
                type="monotone"
                dataKey="earnings"
                stroke="#f59e0b"
                fill="#f59e0b"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hourly Activity</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.hourlyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="hour" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} />
              <Tooltip />
              <Bar dataKey="clicks" fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Device Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Device Types</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={stats.topDevices}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="percentage"
                label={({ device, percentage }) => `${device} ${percentage}%`}
              >
                {stats.topDevices.map((device, index) => (
                  <Cell key={`cell-${device.device}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Countries */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Globe className="w-5 h-5 mr-2 text-gray-600" />
            Top Countries
          </h2>
          <div className="space-y-3">
            {stats.topCountries.map((country, idx) => (
              <div key={country.country} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-500 mr-3">#{idx + 1}</span>
                  <span className="text-sm font-medium text-gray-900">{country.country}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {country.clicks.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">${country.earnings.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Browser Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Monitor className="w-5 h-5 mr-2 text-gray-600" />
            Browsers
          </h2>
          <div className="space-y-3">
            {stats.browserStats.map((browser) => (
              <div key={browser.browser}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{browser.browser}</span>
                  <span className="text-sm text-gray-500">{browser.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full"
                    style={{ width: `${browser.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* OS Stats */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Smartphone className="w-5 h-5 mr-2 text-gray-600" />
            Operating Systems
          </h2>
          <div className="space-y-3">
            {stats.osStats.map((os) => (
              <div key={os.os}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{os.os}</span>
                  <span className="text-sm text-gray-500">{os.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${os.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Average EPC</p>
            <p className="text-2xl font-bold text-gray-900">${stats.averageEPC.toFixed(3)}</p>
            <p className="text-xs text-gray-500 mt-1">Earnings per click</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Total Earnings</p>
            <p className="text-2xl font-bold text-gray-900">${stats.earnings.toFixed(2)}</p>
            <p className="text-xs text-gray-500 mt-1">In selected period</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Performance Score</p>
            <p className="text-2xl font-bold text-gray-900">
              {Math.min(100, Math.round(stats.conversionRate * 20))}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Based on conversion rate</p>
          </div>
        </div>
      </div>
    </div>
  )
}
